import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CountryCodeSelect, countryCodes } from "@/lib/country-code-select";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useForm, type Path } from "react-hook-form";
import countryList from "react-select-country-list";
import { z } from "zod";
import { useSubmitKyc } from "@/hooks/use-mutations";
import { toSentenceCase } from "@/lib/utils";
import {
  useGetCardholderCities,
  useGetCardholderCountries,
} from "@/hooks/use-queries";
import Throbber from "@/components/throbber";
import UploadField from "./upload-field";

const DOC_TYPES = [
  { value: "passport", label: "Passport" },
  { value: "driver_licence", label: "Driver Licence" },
  { value: "national_id", label: "National" },
] as const;

const ID_TYPES = {
  passport: "PASSPORT",
  driver_licence: "DRIVERS_LICENSE",
  national_id: "GOVERNMENT_ISSUED_ID_CARD",
} as const;

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
];

const today = () => new Date().toISOString().slice(0, 10);

// Latest birth date that is still 18 or over.
const latestAdultDob = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().slice(0, 10);
};

const file = (message: string) =>
  z.custom<File>((v) => v instanceof File, { message });

const schema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(2, "First name must be at least 2 characters")
      .max(32, "First name must be at most 32 characters"),
    lastName: z
      .string()
      .trim()
      .min(2, "Last name must be at least 2 characters")
      .max(32, "Last name must be at most 32 characters"),
    email: z
      .string()
      .trim()
      .min(1, "Email is required")
      .email("Invalid email address")
      .max(50, "Email must be at most 50 characters"),
    dialCode: z.string().min(1, "Select a country code"),
    phone: z
      .string()
      .trim()
      .regex(/^[0-9\s]{6,15}$/, "Enter a valid phone number"),
    dob: z
      .string()
      .min(1, "Date of birth is required")
      .refine(
        (v) => v <= latestAdultDob(),
        "You must be at least 18 years old",
      ),
    nationality: z.string().min(1, "Nationality is required"),
    gender: z.string().min(1, "Gender is required"),

    street: z
      .string()
      .trim()
      .min(2, "Street address must be at least 2 characters")
      .max(100, "Street address must be at most 100 characters"),
    apartment: z.string().optional(),
    country: z.string().min(1, "Country is required"),
    state: z.string().trim().min(1, "State is required"),
    city: z.string().trim().min(1, "City is required"),
    postalCode: z
      .string()
      .trim()
      .min(2, "Postal code must be at least 2 characters")
      .max(15, "Postal code must be at most 15 characters"),
    proofOfAddress: file("Proof of address is required"),

    docType: z.enum(["passport", "driver_licence", "national_id"]),
    docNumber: z
      .string()
      .trim()
      .min(2, "Document number must be at least 2 characters")
      .max(50, "Document number must be at most 50 characters"),
    issueDate: z
      .string()
      .min(1, "Issue date is required")
      .refine((v) => v <= today(), "Issue date can't be in the future"),
    idExpiryDate: z
      .string()
      .min(1, "Expiry date is required")
      .refine((v) => v > today(), "This document has expired"),
    docFront: file("This document is required"),
    docBack: z.custom<File>().optional(),
    selfie: file("A selfie is required"),
  })
  .refine(
    (d) => [d.street, d.apartment].filter(Boolean).join(", ").length <= 100,
    {
      path: ["street"],
      message:
        "Street address and apartment must be 100 characters or less combined",
    },
  )
  .refine((d) => d.idExpiryDate > d.issueDate, {
    path: ["idExpiryDate"],
    message: "Expiry must be after the issue date",
  })
  .refine((d) => d.docType === "passport" || d.docBack instanceof File, {
    path: ["docBack"],
    message: "The back of the document is required",
  });

type FormData = z.infer<typeof schema>;

const DOC_TYPE_FROM_API: Record<string, FormData["docType"]> = {
  PASSPORT: "passport",
  DLN: "driver_licence",
  DRIVERS_LICENSE: "driver_licence",
  NATIONAL_ID: "national_id",
  GOVERNMENT_ISSUED_ID_CARD: "national_id",
};

// Everything except the files, so a rejected user only fixes what was wrong.
const fromSubmission = (prev: KycSubmission): Partial<FormData> => {
  const dialDigits = prev.areaCode?.replace(/\D/g, "");
  return {
    dialCode: dialDigits || undefined,
    phone: prev.phone ?? undefined,
    dob: prev.birthday,
    nationality: prev.nationality
      ? countryList().getValue(prev.nationality)
      : undefined,
    gender: prev.gender,
    street: prev.address,
    country: prev.country,
    state: prev.state,
    city: prev.city,
    postalCode: prev.postalCode,
    docType: (prev.idType && DOC_TYPE_FROM_API[prev.idType]) || undefined,
    docNumber: prev.idNumber,
    issueDate: prev.issueDate,
    idExpiryDate: prev.idExpiryDate,
  };
};

const STEPS: { label: string; fields: Path<FormData>[] }[] = [
  {
    label: "Personal",
    fields: [
      "firstName",
      "lastName",
      "email",
      "dialCode",
      "phone",
      "dob",
      "nationality",
      "gender",
    ],
  },
  {
    label: "Address",
    fields: [
      "street",
      "country",
      "state",
      "city",
      "postalCode",
      "proofOfAddress",
    ],
  },
  {
    label: "Identity",
    fields: [
      "docType",
      "docNumber",
      "issueDate",
      "idExpiryDate",
      "docFront",
      "docBack",
      "selfie",
    ],
  },
];

const fieldBg =
  "border border-[#CECECE2E] bg-[linear-gradient(180deg,rgba(255,255,255,0.1)_0%,rgba(153,153,153,0.1)_100%)]";
const inputClass = `h-11 rounded-xl ${fieldBg} placeholder:text-dark-text-300`;
const triggerClass = `!h-11 w-full overflow-hidden rounded-xl ${fieldBg} data-[placeholder]:text-dark-text-300`;
const labelClass = "text-xs";
const ACTIVE_BG = "bg-[#2F2F2F]";

const Required = () => <span className="text-red-500">*</span>;

const KycForm = ({
  defaults,
  previous,
  onBackToIntro,
  onSubmitted,
}: {
  defaults?: { firstName?: string; lastName?: string; email?: string };
  // The rejected submission, when resubmitting.
  previous?: KycSubmission;
  onBackToIntro: () => void;
  onSubmitted: () => void;
}) => {
  const [step, setStep] = useState(0);
  const countries = useMemo(() => countryList().getData(), []);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: defaults?.firstName ?? "",
      lastName: defaults?.lastName ?? "",
      email: defaults?.email ?? "",
      dialCode: "1",
      phone: "",
      dob: "",
      nationality: "",
      gender: "",
      street: "",
      apartment: "",
      country: "",
      state: "",
      city: "",
      postalCode: "",
      docType: "passport",
      docNumber: "",
      issueDate: "",
      idExpiryDate: "",
      ...(previous
        ? Object.fromEntries(
            Object.entries(fromSubmission(previous)).filter(
              ([, v]) => v !== undefined && v !== "",
            ),
          )
        : {}),
    },
  });

  const setDialCode = useCallback(
    (dialCode: string) => form.setValue("dialCode", dialCode),
    [form],
  );

  // Address options come from the same endpoints the cardholder form uses.
  const { data: addressCountries, isLoading: isLoadingCountries } =
    useGetCardholderCountries();
  const addressCountryOptions = useMemo(
    () =>
      (addressCountries?.data ?? []).map(
        (c: { code: string; name: string }) => ({
          value: c.code,
          label: c.name,
        }),
      ),
    [addressCountries],
  );
  const watchedCountry = form.watch("country");
  const watchedState = form.watch("state");
  const { data: regionsData, isLoading: isLoadingRegions } =
    useGetCardholderCities(watchedCountry);
  const regions: {
    name: string;
    children: { name: string }[];
  }[] = regionsData?.data ?? [];
  const stateOptions = regions.map((r) => ({ value: r.name, label: r.name }));
  const cityOptions = (
    regions.find((r) => r.name === watchedState)?.children ?? []
  ).map((c) => ({ value: c.name, label: c.name }));

  const docType = form.watch("docType");
  const docLabel = DOC_TYPES.find((d) => d.value === docType)!.label;

  const next = async () => {
    if (!(await form.trigger(STEPS[step].fields))) return;
    setStep(step + 1);
  };

  const back = () => (step === 0 ? onBackToIntro() : setStep(step - 1));

  const { mutate: submitKyc, isPending: isSubmitting } = useSubmitKyc({
    onSuccess: onSubmitted,
  });

  const onSubmit = (d: FormData) =>
    submitKyc({
      files: {
        utilityBill: d.proofOfAddress,
        idDocument: d.docFront,
        idDocumentBack: d.docType === "passport" ? undefined : d.docBack,
        selfie: d.selfie,
      },
      payload: {
        firstName: d.firstName,
        lastName: d.lastName,
        email: d.email,
        phone: d.phone.replace(/\s/g, ""),
        areaCode: `+${d.dialCode}`,
        birthday: d.dob,
        gender: d.gender as "M" | "F",
        nationality: countryList().getLabel(d.nationality),
        country: d.country,
        state: d.state,
        city: d.city,
        address: [d.street, d.apartment].filter(Boolean).join(", "),
        postalCode: d.postalCode,
        idType: ID_TYPES[d.docType],
        idNumber: d.docNumber,
        issueDate: d.issueDate,
        idExpiryDate: d.idExpiryDate,
      },
    });

  const textField = (
    name: Path<FormData>,
    label: string,
    placeholder: string,
    opts: {
      required?: boolean;
      type?: string;
      readOnly?: boolean;
      min?: string;
      max?: string;
    } = {
      required: true,
    },
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex min-w-0 grow flex-col gap-1">
          <FormLabel className={labelClass}>
            {label} {opts.required && <Required />}
          </FormLabel>
          <div className="relative">
            <FormControl>
              <Input
                className={`${inputClass} ${opts.readOnly ? "cursor-not-allowed opacity-60" : ""} ${opts.type === "date" ? "block h-9 w-full min-w-0 appearance-none text-left [&::-webkit-date-and-time-value]:min-h-0 [&::-webkit-date-and-time-value]:text-left" : ""}`}
                type={opts.type}
                placeholder={placeholder}
                readOnly={opts.readOnly}
                min={opts.min}
                max={opts.max}
                {...field}
                value={(field.value as string) ?? ""}
              />
            </FormControl>
            {/* iOS shows an empty box for an unset date input, so fake the placeholder. */}
            {opts.type === "date" && !field.value && (
              <span className="text-dark-text-300 pointer-events-none absolute top-1/2 left-3 hidden -translate-y-1/2 text-[10px] [@supports(-webkit-touch-callout:none)]:block">
                {placeholder}
              </span>
            )}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const selectField = (
    name: Path<FormData>,
    label: string,
    placeholder: string,
    options: { value: string; label: string }[],
    opts: {
      onChange?: (value: string) => void;
      disabled?: boolean;
      loading?: boolean;
    } = {},
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex min-w-0 grow flex-col gap-1">
          {label && (
            <FormLabel className={labelClass}>
              {label} <Required />
            </FormLabel>
          )}
          <Select
            value={(field.value as string) || undefined}
            disabled={opts.disabled || opts.loading}
            onValueChange={(v) => {
              field.onChange(v);
              opts.onChange?.(v);
            }}
          >
            <FormControl>
              <SelectTrigger className={triggerClass}>
                <SelectValue
                  placeholder={opts.loading ? "Loading…" : placeholder}
                />
              </SelectTrigger>
            </FormControl>
            <SelectContent className="max-h-72">
              {options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const uploadField = (
    name: "proofOfAddress" | "docFront" | "docBack" | "selfie",
    title: string,
    description: string,
    opts: { accept?: string; capture?: "user" } = {},
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field, fieldState }) => (
        <UploadField
          title={title}
          description={description}
          file={field.value as File | undefined}
          error={fieldState.error?.message}
          onChange={field.onChange}
          {...opts}
        />
      )}
    />
  );

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mx-auto mt-6 flex w-full max-w-sm grow flex-col pb-6 lg:mt-10 lg:justify-center"
        >
          {previous?.reviewNote && (
            <div className="mt-6 rounded-xl border border-red-500/30 bg-red-500/5 p-3 text-xs">
              <p className="font-semibold">Reason for rejection</p>
              <p className="mt-0.5">{toSentenceCase(previous.reviewNote)}</p>
            </div>
          )}

          <div className="mt-8">
            <div className="text-dark-text-300 flex items-center justify-between">
              <span className="text-lg">
                Step {step + 1} of {STEPS.length}
              </span>
              <span className="text-sm text-[#2F2F2F]">
                {STEPS[step].label}
              </span>
            </div>
            <div className="mt-2 flex gap-2">
              {STEPS.map((s, i) => (
                <span
                  key={s.label}
                  className={`h-1 grow rounded-full ${i <= step ? ACTIVE_BG : "bg-black/10"}`}
                />
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-4">
            {step === 0 && (
              <>
                <div>
                  <h1 className="text-2xl font-semibold">Personal details</h1>
                  <p className="text-dark-text-300 text-sm">
                    Must match your government-issued ID
                  </p>
                </div>
                <div className="flex gap-2.5 *:basis-1/2">
                  {textField("firstName", "First Name", "Enter First Name", {
                    required: true,
                    readOnly: true,
                  })}
                  {textField("lastName", "Last Name", "Enter Last Name", {
                    required: true,
                    readOnly: true,
                  })}
                </div>
                {textField("email", "Email", "Enter email", {
                  required: true,
                  readOnly: true,
                })}
                <div className="flex flex-col gap-1">
                  <span className={labelClass}>
                    Phone <Required />
                  </span>
                  <div className="flex items-start gap-2.5">
                    <CountryCodeSelect
                      defaultCountry={
                        countryCodes.find(
                          (c) => c.dialCode === form.getValues("dialCode"),
                        )?.code ?? "US"
                      }
                      className={`!h-11 !w-28 rounded-xl ${fieldBg}`}
                      onSelect={setDialCode}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem className="grow">
                          <FormControl>
                            <Input
                              className={inputClass}
                              type="tel"
                              inputMode="tel"
                              placeholder="000 0000 0000"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="flex gap-2.5 *:basis-1/2">
                  {textField("dob", "Date of Birth", "Select DOB", {
                    required: true,
                    type: "date",
                    max: latestAdultDob(),
                  })}
                  {selectField(
                    "nationality",
                    "Nationality",
                    "Select Nationality",
                    countries,
                  )}
                </div>
                {selectField("gender", "Gender", "Select Gender", GENDERS)}
              </>
            )}

            {step === 1 && (
              <>
                <div>
                  <h1 className="text-2xl font-semibold">
                    Residential address
                  </h1>
                  <p className="text-dark-text-300 text-sm">
                    Used for identity verification
                  </p>
                </div>
                {textField("street", "Street Address", "Enter street address")}
                {textField("apartment", "Apartment / Suite", "Optional", {})}
                {selectField(
                  "country",
                  "Country",
                  "Enter Country",
                  addressCountryOptions,
                  {
                    loading: isLoadingCountries,
                    onChange: () => {
                      form.setValue("state", "");
                      form.setValue("city", "");
                    },
                  },
                )}
                {/* Some countries come back with no regions; let those be typed. */}
                {stateOptions.length > 0 || isLoadingRegions
                  ? selectField(
                      "state",
                      "State / Province",
                      "Select state",
                      stateOptions,
                      {
                        disabled: !watchedCountry,
                        loading: isLoadingRegions,
                        onChange: () => form.setValue("city", ""),
                      },
                    )
                  : textField("state", "State / Province", "Enter state")}
                <div className="flex gap-2.5 *:basis-1/2">
                  {cityOptions.length > 0
                    ? selectField("city", "City", "Select city", cityOptions, {
                        disabled: !watchedState,
                      })
                    : textField("city", "City", "Enter city")}
                  {textField("postalCode", "Postal Code", "Enter postal code")}
                </div>
                {uploadField(
                  "proofOfAddress",
                  "Proof of address",
                  "Upload or take photo of Proof of Address.",
                )}
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <h1 className="text-2xl font-semibold">Verify identity</h1>
                  <p className="text-dark-text-300 text-sm">
                    Required under KYC · reviewed within minutes
                  </p>
                </div>
                <FormField
                  control={form.control}
                  name="docType"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-1">
                      <FormLabel className={labelClass}>
                        Document Type <Required />
                      </FormLabel>
                      <div className="grid grid-cols-2 gap-2.5">
                        {DOC_TYPES.map((d) => (
                          <button
                            key={d.value}
                            type="button"
                            onClick={() => {
                              field.onChange(d.value);
                              form.clearErrors("docNumber");
                            }}
                            className={`h-11 rounded-xl border text-sm transition-colors ${field.value === d.value ? `${ACTIVE_BG} border-transparent text-white` : "text-dark-text-300 border-[#CECECE2E]"}`}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </FormItem>
                  )}
                />
                {textField(
                  "docNumber",
                  `${docLabel} Number`,
                  `Enter ${docLabel} number`,
                )}
                <div className="flex gap-2.5 *:basis-1/2">
                  {textField("issueDate", "Issue Date", "Select date", {
                    required: true,
                    type: "date",
                    max: today(),
                  })}
                  {textField("idExpiryDate", "Expiry Date", "Select date", {
                    required: true,
                    type: "date",
                    min: today(),
                  })}
                </div>
                {uploadField(
                  "docFront",
                  docType === "passport"
                    ? "Passport bio data page"
                    : `${docLabel} (front)`,
                  "Upload or take photo of your document.",
                )}
                {docType !== "passport" &&
                  uploadField(
                    "docBack",
                    `${docLabel} (back)`,
                    "Upload or take photo of the back of your document.",
                  )}
                {uploadField(
                  "selfie",
                  "Selfie",
                  "Upload or take photo of yourself.",
                  { accept: "image/*", capture: "user" },
                )}
              </>
            )}
          </div>

          <div className="mt-auto flex gap-3 pt-10 lg:mt-8 lg:pt-0">
            {step < STEPS.length - 1 ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={back}
                  className="h-11 rounded-xl border-[#CECECE2E] bg-transparent px-5 hover:bg-black/5"
                >
                  <ChevronLeft /> Back
                </Button>
                <Button
                  key="continue"
                  type="button"
                  onClick={next}
                  className="h-11 grow rounded-xl bg-[#2F2F2F] text-white hover:bg-[#2F2F2F]/90"
                >
                  Continue <ChevronRight />
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={back}
                  className="h-11 rounded-xl border-[#CECECE2E] bg-transparent px-5 hover:bg-black/5"
                >
                  <ChevronLeft /> Back
                </Button>
                {/* Own key: React would otherwise reuse the Continue button's
                    element and flip it to type="submit" while the click that
                    advanced the step is still being handled, submitting the
                    untouched last step. */}
                <Button
                  key="submit"
                  type="submit"
                  disabled={isSubmitting}
                  className="h-11 grow rounded-xl bg-[#2F2F2F] text-white uppercase hover:bg-[#2F2F2F]/90"
                >
                  {isSubmitting ? <Throbber /> : "Submit KYC"}
                </Button>
              </>
            )}
          </div>
        </form>
      </Form>
    </>
  );
};

export default KycForm;
